import React, { useMemo, useState } from 'react';
import { Button, Card, CardHeader, CardBody, Row, Col, FormGroup, Label, Input, Table } from 'reactstrap';
import Select from 'components/Select';
import DollarInput from '../DollarInput';
import { useUpdateSalesLocation } from '../../api/sales-locations/service';
import { useTranslation } from 'react-i18next';
import { toNumber } from 'utils/currency';
import { getActiveColorStyle } from 'utils/colorUtils';
import { getTheme2FormPageDarkSelectStyles } from 'utils/theme2MultiSelect';
import { useGetChannels } from 'api/channels/service';

// Custom CSS for after-market toggle switches
const afterMarketToggleStyles = `
    .after-market-toggle .custom-toggle input:checked + .custom-toggle-slider:before {
        background-color: #ffffff !important;
    }
    .after-market-toggle .custom-toggle input + .custom-toggle-slider:before {
        background-color: #ffffff !important;
    }
`;

const AfterMarket = ({ data, setFormData, activeColorStyle: activeColorStyleProp, activeColor, isTheme2 = false, salesLocationId }) => {
    const { t } = useTranslation();
    const activeColorStyle = useMemo(
        () => activeColorStyleProp || getActiveColorStyle(activeColor),
        [activeColorStyleProp, activeColor]
    );
    const selectStyles = useMemo(
        () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const updateSalesLocationMutation = useUpdateSalesLocation();
    const { data: vendorCompanyData } = useGetChannels("vendor_company");
    const [editingAfterMarketIndex, setEditingAfterMarketIndex] = useState(null);
    const [currentAfterMarket, setCurrentAfterMarket] = useState({
        name: '',
        product_price: '0',
        product_cost: '0',
        terms: 0,
        mileage: 0,
        is_create_bill: false,
        vendor: '',
        value: true
    });

    const vendorCompanyOptions = useMemo(() => {
        if (!vendorCompanyData?.results) return [];
        return vendorCompanyData.results.map((vendor) => ({
            value: vendor.id,
            label: vendor.name,
        }));
    }, [vendorCompanyData]);
    const handleDollarChange = (fieldName) => (value) => {
        setCurrentAfterMarket(prev => ({ ...prev, [fieldName]: value }));
    };

    const handleUpdateAfterMarket = () => {
        if (editingAfterMarketIndex !== null && salesLocationId) {
            const updatedProducts = [...data];

            const priceNum = toNumber(currentAfterMarket.product_price);
            const costNum = toNumber(currentAfterMarket.product_cost);

            const updatedProduct = {
                ...currentAfterMarket,
                product_price: priceNum || 0,
                product_cost: costNum || 0,
                value: data[editingAfterMarketIndex].value // Keep original value, don't change it
            };


            updatedProducts[editingAfterMarketIndex] = updatedProduct;
            setFormData(prev => ({ ...prev, after_market: updatedProducts }));

            // Update sales location with new after_market data
            updateSalesLocationMutation.mutate({
                id: salesLocationId,
                data: {
                    after_market: updatedProducts
                }
            });
        }
        // setEditingAfterMarketIndex(null); // Formu kapatma
    };

    const handleEditAfterMarket = (index) => {
        // If clicking on the same row that's already open, close it
        if (editingAfterMarketIndex === index) {
            setEditingAfterMarketIndex(null);
            return;
        }

        // Close any currently open form first
        setEditingAfterMarketIndex(null);

        // Then open the new form
        setTimeout(() => {
            const productData = data[index];
            setCurrentAfterMarket({
                name: productData.name || '',
                product_price: productData.product_price?.toString() || '0',
                product_cost: productData.product_cost?.toString() || '0',
                terms: productData.terms || 0,
                mileage: productData.mileage || 0,
                is_create_bill: productData.is_create_bill || false,
                vendor: productData.vendor || '',
                value: productData.value !== undefined ? productData.value : true
            });
            setEditingAfterMarketIndex(index);
        }, 0);
    };

    const handleCancelAfterMarket = () => {
        setEditingAfterMarketIndex(null);
    };

    const handleToggleAfterMarketActive = (index) => {
        const updatedProducts = [...data];
        updatedProducts[index] = { ...updatedProducts[index], value: !updatedProducts[index].value };
        setFormData(prev => ({ ...prev, after_market: updatedProducts }));

        // If the product is being set to inactive (false), close any open edit form for this product
        if (!updatedProducts[index].value && editingAfterMarketIndex === index) {
            setEditingAfterMarketIndex(null);
        }

        // Update sales location immediately when switch is toggled
        if (salesLocationId) {
            updateSalesLocationMutation.mutate({
                id: salesLocationId,
                data: {
                    after_market: updatedProducts
                }
            });
        }
    };

    return (
        <div className="after-market-toggle">
            <style>{afterMarketToggleStyles}</style>
            {data?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    No after market products available
                </div>
            ) : (
                <Table responsive>
                    <thead className="text-primary">
                        <tr>
                            <th>Active</th>
                            <th>Display Name</th>
                            <th className="text-right">Edit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.map((product, idx) => (
                            <React.Fragment key={idx}>
                                <tr onClick={() => { product.value && handleEditAfterMarket(idx) }}>
                                    <td>
                                        <label className="custom-toggle">
                                            <input
                                                type="checkbox"
                                                checked={product.value}
                                                onChange={() => handleToggleAfterMarketActive(idx)}
                                            />
                                            <span
                                                className="custom-toggle-slider rounded-circle"
                                                style={{
                                                    backgroundColor: product.value ? '#28a745' : '#6c757d',
                                                    borderColor: product.value ? '#28a745' : '#6c757d'
                                                }}
                                            />
                                        </label>
                                    </td>
                                    <td>{t(`channelsAdd.afterMarket.${product.name}`)}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Button
                                            style={{ ...activeColorStyle, color: 'white' }}
                                            size="sm"
                                            onClick={() => handleEditAfterMarket(idx)}
                                            disabled={!product.value}
                                        >
                                            <i className="tim-icons icon-pencil" /> Edit
                                        </Button>
                                    </td>
                                </tr>
                                {/* Edit Form Row */}
                                {editingAfterMarketIndex === idx && (
                                    <tr>
                                        <td colSpan="4" style={{ padding: '10px', }}>
                                            <Row>
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className='form-control-label'>After Market Product Price:</Label>
                                                        <DollarInput
                                                            name="product_price"
                                                            value={currentAfterMarket.product_price || ''}
                                                            onChange={handleDollarChange('product_price')}
                                                            placeholder="$0.00"
                                                        />
                                                    </FormGroup>
                                                </Col>

                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className='form-control-label'>After Market Product Cost:</Label>
                                                        <DollarInput
                                                            name="product_cost"
                                                            value={currentAfterMarket.product_cost || ''}
                                                            onChange={handleDollarChange('product_cost')}
                                                            placeholder="$0.00"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className='form-control-label'>Terms:</Label>
                                                        <Input
                                                            type="number"
                                                            value={currentAfterMarket.terms}
                                                            onChange={(e) => setCurrentAfterMarket(prev => ({ ...prev, terms: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className='form-control-label'>Mileage:</Label>
                                                        <Input
                                                            type="number"
                                                            value={currentAfterMarket.mileage}
                                                            onChange={(e) => setCurrentAfterMarket(prev => ({ ...prev, mileage: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 }))}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row >
                                                <Col md="6">
                                                    <FormGroup>
                                                        <Label className='form-control-label'>Vendor:</Label>
                                                        <Select
                                                            activeColor={activeColor}
                                                            styles={selectStyles}
                                                            className="react-select"
                                                            name="vendor"
                                                            placeholder="Select vendor"
                                                            value={vendorCompanyOptions.find(opt => opt.value === currentAfterMarket.vendor)}
                                                            onChange={(option) => setCurrentAfterMarket(prev => ({ ...prev, vendor: option?.value || '' }))}
                                                            options={vendorCompanyOptions}
                                                            isClearable
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md="6" className='d-flex align-items-center'>
                                                    <FormGroup>
                                                        <Label className='form-control-label' style={{ marginRight: "30px" }}>Create Bill:</Label>
                                                        <Input
                                                            id="is_create_bill"
                                                            style={{ borderRadius: 0, fontSize: "14px" }}
                                                            name="is_create_bill"
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={!!currentAfterMarket.is_create_bill}
                                                            onChange={(e) => setCurrentAfterMarket(prev => ({ ...prev, is_create_bill: e.target.checked }))}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col className="text-right">
                                                    <Button
                                                        color="secondary"
                                                        size="sm"
                                                        onClick={handleCancelAfterMarket}
                                                        style={{ marginRight: '10px' }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        style={{ ...activeColorStyle, color: 'white' }}
                                                        onClick={handleUpdateAfterMarket}
                                                    >
                                                        Update
                                                    </Button>
                                                </Col>
                                            </Row>

                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
};

export default AfterMarket;

